import { cloneDeep } from 'lodash-es';
import {
  CalendarTypes,
  type CalendarEvent,
  type CalendarObject,
  type StructuredStationEvents,
  type CalendarEventSchedule,
  type CalendarEventData,
  type TimeRange,
  MenuType,
  type CalendarEventIs,
} from '@centric-os/types';
import { useDateHelper } from '@centric-os/helpers';
import { DateTime, Info } from 'luxon';
import { useStationEventsForCalendar } from '..';
import {
  RepeatByValues,
  RepeatValues,
} from '../../../routes/station-schedule/components/schedule-panel/menu-schedule-panel/enums';
import { ORDER_TYPES } from '@/views/app-modules/orders/src/enums';

export default () => {
  const { toMinutes, addNextDay, isOvernight } = useDateHelper();

  const structureCalendarEvents = (calendar: CalendarObject): StructuredStationEvents => {
    if (!calendar) return null;
    return calendar?.events.reduce(
      (stationEventAcc: StructuredStationEvents, stationEvent: CalendarEvent) => {
        if (stationEvent?.data?.calendar === CalendarTypes.MENU) {
          const keyExists = Array.isArray(stationEventAcc.menuEvents?.[stationEvent?.data?.menu]);
          // If key exists, spread onto current array, if not, create new array.
          stationEventAcc.menuEvents[stationEvent?.data?.menu] = [
            ...(keyExists ? stationEventAcc.menuEvents[stationEvent?.data?.menu] : []),
            stationEvent,
          ];
        }
        if (stationEvent?.data?.calendar === CalendarTypes.BUSINESS)
          stationEventAcc.businessEvents.push(stationEvent);
        if (stationEvent?.data?.calendar === CalendarTypes.HOLIDAY)
          stationEventAcc.closureEvents.push(stationEvent);
        if (stationEvent?.data?.calendar === CalendarTypes.DELIVERY)
          stationEventAcc.deliveryEvents.push(stationEvent);
        return stationEventAcc;
      },
      { menuEvents: {}, businessEvents: [], closureEvents: [], deliveryEvents: [] },
    );
  };

  const todaysEvents = (calendarEvents: CalendarEvent[], timezone?: string): CalendarEvent[] => {
    const todaysDate = DateTime.now().setZone(timezone ?? 'local');
    const todaysEvents = calendarEvents?.filter((menuEvent) => {
      // Check if events start date has passed, but not the end date.
      if (
        !isEventCurrent(todaysDate.toMillis(), menuEvent?.schedule?.start, menuEvent?.schedule?.end)
      )
        return false;
      // Check if event is not reoccuring
      if (isOneDayEvent(menuEvent)) {
        if (isOneDayEventScheduledForToday(menuEvent, todaysDate, timezone)) return true;
        return false;
      }
      if (isReoccuringEventScheduledForToday(menuEvent, todaysDate, timezone)) return true;
      return false;
    });
    return todaysEvents;
  };

  const isOneDayEvent = (calendarEvent: CalendarEvent): boolean => {
    const dayOfMonth = calendarEvent?.schedule?.dayOfMonth?.[0];
    const month = calendarEvent?.schedule?.month?.[0];
    const year = calendarEvent?.schedule?.year?.[0];
    const startDate = calendarEvent?.schedule?.start;
    const endDate = calendarEvent?.schedule?.end;
    if (endDate - startDate < 86400000) return true;
    if (year && month && dayOfMonth) return true;
    return false;
  };

  const isOneDayEventScheduledForToday = (
    calendarEvent: CalendarEvent,
    todaysDate: DateTime,
    timezone?: string,
  ): boolean => {
    const dayOfMonth = calendarEvent?.schedule?.dayOfMonth?.[0];
    const legacyMonth = calendarEvent?.schedule?.month?.[0];
    const year = calendarEvent?.schedule?.year?.[0];
    // A one-day range without the legacy year/month/dayOfMonth fields is today's whenever it is current.
    if (year === undefined || legacyMonth === undefined || dayOfMonth === undefined) return true;
    const month = legacyMonth <= 10 ? legacyMonth + 1 : 12; // Calendar: 11 === DEC || Luxon: 12 === DEC. Adjust for compatability
    const menuDate = DateTime.fromObject(
      { year, month, day: dayOfMonth },
      { zone: timezone ?? 'local' },
    );
    if (menuDate.startOf('day').toMillis() === todaysDate.startOf('day').toMillis()) return true;
    return false;
  };

  const isEventCurrent = (currentTS: number, startTS: number, endTS: number): boolean => {
    if (currentTS < startTS) return false;
    if (endTS && currentTS > endTS) return false;
    return true;
  };

  const getStartOfWeekTSFromMillis = (millis: number, timezone?: string): number | null => {
    if (!millis) return null;
    let time = DateTime.fromMillis(millis, { zone: timezone ?? 'local' });
    if (time.weekday === 7) time = time.startOf('day');
    if (time.weekday !== 7) time = time.startOf('week').minus({ days: 1 });
    return time.toMillis();
  };

  const isReoccuringEventScheduledForToday = (
    calendarEvent: CalendarEvent,
    todaysDate: DateTime,
    timezone?: string,
  ): boolean => {
    // @ts-expect-error FIXME: Property 'week' does not exist on type 'boolean | { week: number; }'.
    const nWeeks = calendarEvent?.schedule?.every?.week || 1;
    const menuEventStartWeek = getStartOfWeekTSFromMillis(calendarEvent?.schedule?.start, timezone);
    const startOfCurrentWeek = getStartOfWeekTSFromMillis(todaysDate.toMillis(), timezone);
    if (!menuEventStartWeek === null) return false;
    const millisecondsPerWeek = 1000 * 60 * 60 * 24 * 7;
    const weeksElapsed = Math.floor(
      (startOfCurrentWeek - menuEventStartWeek) / millisecondsPerWeek,
    );
    if (weeksElapsed % nWeeks !== 0) return false;
    const daysOfWeek: number[] = calendarEvent?.schedule?.dayOfWeek;
    return daysOfWeek?.includes(todaysDate.weekday) || !daysOfWeek;
  };

  // @ts-expect-error FIXME: Parameter 'event' implicitly has an 'any' type.
  const getRepeatMessageFromEvent = (event) => {
    const byWeekday = event.byWeekday;
    const repeatEvery = event.repeatEvery;
    let repeatByText: string;
    if (event.repeatBy === RepeatByValues.WEEK) repeatByText = repeatEvery > 1 ? 'weeks' : 'week';
    if (event.repeatBy === RepeatByValues.MONTH)
      repeatByText = repeatEvery > 1 ? 'months' : 'month';
    const luxonDays = byWeekday.map((day: number) => (day === 0 ? 6 : day - 1)).toSorted();
    return luxonDays.reduce((acc: string, day: number, index: number) => {
      acc += `${Info.weekdaysFormat('short')[day]}`;
      if (index < luxonDays.length - 1) {
        acc += ', ';
      } else {
        acc += ` every ${repeatEvery > 1 ? `${repeatEvery} ` : ''}${repeatByText}`;
      }
      return acc;
    }, '');
  };

  const everyWeekDaySchedule = [1, 2, 3, 4, 5];

  const getRepeatValueFromSchedule = (schedule: CalendarEventSchedule) => {
    const isEveryDay = Boolean(!schedule.dayOfWeek?.length);
    const isEveryWorkDay = Boolean(
      schedule.dayOfWeek?.length === 5 &&
        everyWeekDaySchedule.every((day) => schedule.dayOfWeek?.includes(day)),
    );
    if (isEveryDay) return RepeatValues.EVERYDAY;
    if (isEveryWorkDay) return RepeatValues.WEEKDAY;
    const repeatEveryPeriod: string = schedule.every && Object.keys(schedule.every)[0];
    return getRepeatMessageFromEvent({
      byWeekday: schedule.dayOfWeek,
      // @ts-expect-error FIXME: Element implicitly has an 'any' type because expression of type 'string' can'...
      repeatEvery: schedule.every?.[repeatEveryPeriod] || 1,
      repeatBy: RepeatByValues.WEEK,
    });
  };
  // @ts-expect-error FIXME: Parameter 'formData' implicitly has an 'any' type.
  const prepMenuEventForSave = (formData, currentEvent?: CalendarEvent, timezone?: string) => {
    const {
      eventMenu,
      startDate,
      endDate,
      startTime,
      endTime,
      repeat,
      pickup,
      byWeekday,
      hidden,
      delivery,
      noEndDate,
      repeatInterval,
    } = cloneDeep(formData);

    // The pickers hold wall-clock times; their difference is the menu's daily hours whatever the DST offset
    const startTimeOfDay = DateTime.fromJSDate(startTime);
    const endTimeOfDay = DateTime.fromJSDate(endTime);
    let durationInMinutes = toMinutes(endTimeOfDay) - toMinutes(startTimeOfDay);
    if (durationInMinutes <= 0) {
      durationInMinutes += 1440; // ends on the next day
    }

    // The picker dates carry the station's wall clock; the instants must be in the station's zone
    const zone = timezone ?? 'local';
    const startOfStartDate = DateTime.fromJSDate(startDate)
      .setZone(zone, { keepLocalTime: true })
      .startOf('day');
    const endOfEndDate = DateTime.fromJSDate(endDate)
      .setZone(zone, { keepLocalTime: true })
      .endOf('day');

    const data: Partial<CalendarEventData> = {
      calendar: CalendarTypes.MENU,
      id: currentEvent?.data?.id,
      menu: eventMenu?.id,
      title: eventMenu?.name,
      is: {
        delivery: delivery,
        pickup: pickup,
        disabled: hidden,
        // right now we don't have logic to control this value but at least preserve it if exists
        frictionless: currentEvent?.data?.is?.frictionless,
      },
      version: eventMenu?.meta?.menu_type === MenuType.CENTRIC_OS ? 3 : eventMenu?.meta?.version,
    };
    const schedule: Partial<CalendarEventSchedule> = {
      start: startOfStartDate.toMillis(),
      end: noEndDate ? undefined : endOfEndDate.toMillis(),
      dayOfWeek: byWeekday?.length ? byWeekday : undefined,
      duration: durationInMinutes,
      times: [startTimeOfDay.toFormat('HH:mm')],
      every: repeatInterval !== RepeatValues.EVERYDAY && repeat,
      durationUnit: 'minutes',
    };
    return { data, schedule } as CalendarEvent;
  };

  // @ts-expect-error FIXME: Parameter 'formData' implicitly has an 'any' type.
  const prepClosureEventForSave = (formData, currentEvent?: CalendarEvent, timezone?: string) => {
    const { startDateTime, endDateTime, reason } = formData;
    // The pickers carry the station's wall clock; the instants must be in the station's zone
    const zone = timezone ?? 'local';
    const startTimeDate = DateTime.fromJSDate(startDateTime).setZone(zone, { keepLocalTime: true });
    const endTimeDate = DateTime.fromJSDate(endDateTime).setZone(zone, { keepLocalTime: true });

    const data: Partial<CalendarEventData> = {
      calendar: CalendarTypes.HOLIDAY,
      id: currentEvent?.data?.id,
      title: reason,
      is: {},
    };
    const schedule: Partial<CalendarEventSchedule> = {
      start: startTimeDate.toMillis(),
      end: endTimeDate.toMillis(),
    };
    return { data, schedule } as CalendarEvent;
  };

  const checkEventsOverlap = (
    event1Start: DateTime,
    event1End: DateTime,
    event2Start: DateTime,
    event2End: DateTime,
    event1Weekdays?: number[],
    event2Weekdays?: number[],
  ) => {
    const minutesInDay = 1440;
    const daysInWeek = 7;

    // If no weekdays exist for either event, just do a quick overlap check
    if (!event1Weekdays && !event2Weekdays) {
      // Handle an overnight scenario
      if (event1End <= event1Start) {
        event1End = event1End.plus({ day: 1 });
      }
      if (event2End <= event2Start) {
        event2End = event2End.plus({ day: 1 });
      }
      return (
        (event1Start < event2End && event1End > event2Start) ||
        (event1Start >= event2End && event1End > event2Start.plus({ day: 1 }))
      );
    }

    // Sort weekdays in ascending order
    event1Weekdays = [...event1Weekdays].toSorted((a, b) => a - b);
    event2Weekdays = [...event2Weekdays].toSorted((a, b) => a - b);

    // Early exit if both events have specific weekdays and none overlap
    const hasCommonDay = event1Weekdays.some((day) => event2Weekdays.includes(day));
    if (!hasCommonDay) {
      return false; // No common weekdays, they can't overlap
    }

    // Only keep event1 weekdays that fall between event1Start and event1End
    const daysInRange: number[] = [];
    for (let i = 0; i < daysInWeek; i++) {
      const checkDay = event1Start.plus({ days: i });
      if (checkDay > event1End) break;
      daysInRange.push(checkDay.weekday % daysInWeek);
    }

    // Convert event times to numerical ranges (minutes since midnight)
    const range1: TimeRange = { start: toMinutes(event1Start), end: toMinutes(event1End) };
    const range2: TimeRange = { start: toMinutes(event2Start), end: toMinutes(event2End) };

    // Update weekdays for overnight spans
    const updatedEvent1Weekdays = addNextDay(event1Weekdays, range1).filter((day) =>
      daysInRange.includes(day),
    );
    const updatedEvent2Weekdays = addNextDay(event2Weekdays, range2).filter((day) =>
      daysInRange.includes(day),
    );

    // Find shared weekdays - for recurring events, we only care about time and day of week
    const commonWeekdays = updatedEvent1Weekdays.filter((day) =>
      updatedEvent2Weekdays.includes(day),
    );
    if (commonWeekdays.length === 0) return false;

    // Check overlaps for each shared weekday
    for (const _day of commonWeekdays) {
      let normalizedRange1 = { ...range1 };
      let normalizedRange2 = { ...range2 };

      if (isOvernight(range1)) {
        normalizedRange1.end += minutesInDay;
      }
      if (isOvernight(range2)) {
        normalizedRange2.end += minutesInDay;
        // If event2 spans midnight and event1 starts the next day
        if (range1.start < range2.end) {
          normalizedRange1.start += minutesInDay;
          normalizedRange1.end += minutesInDay;
        }
      }

      // Final check for an overlap
      const finalOverlap =
        normalizedRange1.start < normalizedRange2.end &&
        normalizedRange1.end > normalizedRange2.start;
      if (finalOverlap) return true;
    }

    return false;
  };

  const formOverlapsWithMenuEvent = (
    // @ts-expect-error FIXME: Parameter 'formValues' implicitly has an 'any' type.
    formValues,
    currentEvent: CalendarEvent,
    menuEvents: CalendarEvent[],
  ): boolean => {
    if (!menuEvents) return false;
    const { noEndDate, startDate, endDate, startTime, endTime, byWeekday, pickup, delivery } =
      formValues;

    const formStartDate = DateTime.fromJSDate(startDate).startOf('day');
    let formEndDate = endDate ? DateTime.fromJSDate(endDate).startOf('day') : formStartDate;

    if (noEndDate) formEndDate = formStartDate.plus({ year: 1000 });

    const formStartDateTime = formStartDate
      .set({
        hour: DateTime.fromJSDate(startTime).hour,
        minute: DateTime.fromJSDate(startTime).minute,
        second: DateTime.fromJSDate(startTime).second,
        millisecond: DateTime.fromJSDate(startTime).millisecond,
      })
      .setZone('utc', { keepLocalTime: true });

    const formEndDateTime = formEndDate
      .set({
        hour: DateTime.fromJSDate(endTime).hour,
        minute: DateTime.fromJSDate(endTime).minute,
        second: DateTime.fromJSDate(endTime).second,
        millisecond: DateTime.fromJSDate(endTime).millisecond,
      })
      .setZone('utc', { keepLocalTime: true });

    const formWeekDays = byWeekday?.length ? byWeekday : [0, 1, 2, 3, 4, 5, 6];

    const { getEventDateStart, getEventDuration } = useStationEventsForCalendar();

    let overlappingEvent = false;

    for (const menuEvent of menuEvents) {
      if (menuEvent?.data?.id === currentEvent?.data?.id) continue;

      const menuEventPickup: boolean = menuEvent.data?.is?.pickup;
      const menuEventDelivery: boolean = menuEvent.data?.is?.delivery;
      const isOrderTypeOverlap: boolean =
        (pickup && menuEventPickup) || (delivery && menuEventDelivery);

      // Get event start date-time from the event schedule
      const eventStartDateTime = DateTime.fromISO(getEventDateStart(menuEvent?.schedule)).setZone(
        'utc',
      );
      const eventDuration = getEventDuration(menuEvent?.schedule);

      // Calculate event end time by adding event duration to start time
      const eventEndTime = eventStartDateTime.plus({ millisecond: eventDuration });

      // Get the event end date or use far future if no end date is set
      const scheduleEndDate = menuEvent?.schedule?.end
        ? DateTime.fromMillis(menuEvent?.schedule?.end).startOf('day').setZone('utc')
        : formEndDate.plus({ year: 1000 });

      // Combine: Use end date from schedule but time from calculated end time
      const eventEndDateTime = scheduleEndDate.set({
        hour: eventEndTime.hour,
        minute: eventEndTime.minute,
        second: eventEndTime.second,
        millisecond: eventEndTime.millisecond,
      });

      // Use weekdays from the event schedule or default to all days
      const eventWeekdays = menuEvent?.schedule?.dayOfWeek || [0, 1, 2, 3, 4, 5, 6];

      if (
        checkEventsOverlap(
          formStartDateTime,
          formEndDateTime,
          eventStartDateTime,
          eventEndDateTime,
          formWeekDays,
          eventWeekdays,
        ) &&
        isOrderTypeOverlap
      ) {
        overlappingEvent = true;
        break;
      }
    }

    return overlappingEvent;
  };

  const formOverlapsWithClosureEvent = (
    // @ts-expect-error FIXME: Parameter 'formValues' implicitly has an 'any' type.
    formValues,
    currentEvent: CalendarEvent,
    closureEvents: CalendarEvent[],
    timezone?: string,
  ) => {
    if (!closureEvents) return false;

    const { startDateTime, endDateTime } = formValues;

    // The pickers carry the station's wall clock; compare instants in the station's zone
    const zone = timezone ?? 'local';
    const formStartDateTime = DateTime.fromJSDate(startDateTime).setZone(zone, {
      keepLocalTime: true,
    });
    const formEndDateTime = DateTime.fromJSDate(endDateTime).setZone(zone, {
      keepLocalTime: true,
    });

    let overlappingEvent = false;
    for (const closureEvent of closureEvents) {
      if (closureEvent?.data?.id === currentEvent?.data?.id) continue;

      if (closureEvent?.schedule?.start && closureEvent?.schedule?.end) {
        const closureEventStartDateTime = DateTime.fromMillis(closureEvent?.schedule?.start);
        const closureEventEndDateTime = DateTime.fromMillis(closureEvent?.schedule?.end);

        if (formStartDateTime >= closureEventEndDateTime) continue;

        if (
          checkEventsOverlap(
            formStartDateTime,
            formEndDateTime,
            closureEventStartDateTime,
            closureEventEndDateTime,
          )
        ) {
          overlappingEvent = true;
          break;
        }
      }
    }
    return overlappingEvent;
  };

  // Check if events have the same order types
  // Events overlap if they share at least one order type OR both have no order types
  const eventsOverlapByOrderType = (
    eventOrderTypes: CalendarEventIs,
    comparisonOrderTypes: CalendarEventIs,
  ): boolean => {
    const orderTypeKeys: (keyof CalendarEventIs)[] = [ORDER_TYPES.DELIVERY, ORDER_TYPES.PICKUP];

    const hasSharedOrderType = orderTypeKeys.some(
      (key) => eventOrderTypes[key] && comparisonOrderTypes[key],
    );

    const hasAnyOrderType = orderTypeKeys.some(
      (key) => eventOrderTypes[key] || comparisonOrderTypes[key],
    );

    return hasSharedOrderType || !hasAnyOrderType;
  };

  const detectEventOverlaps = (
    event: CalendarEvent,
    events: CalendarEvent[],
    timezone?: string,
  ): CalendarEvent[] => {
    const overlaps: CalendarEvent[] = [];

    const { getEventDateStart, getEventDuration } = useStationEventsForCalendar();

    // Get the input event's date/time information
    const eventStartDateTime: DateTime = DateTime.fromISO(
      getEventDateStart(event.schedule, timezone),
    ).setZone('utc');
    const eventDuration: number = getEventDuration(event.schedule);
    const eventEndDateTime: DateTime = eventStartDateTime.plus({ millisecond: eventDuration });

    // Get the input event's overall date range (start and end timestamps)
    const eventRangeStart: number = event.schedule?.start;
    const eventRangeEnd: number = event.schedule?.end;

    // Get weekdays (default to all days if not specified in the event schedule)
    const eventWeekdays: number[] = event.schedule?.dayOfWeek || [0, 1, 2, 3, 4, 5, 6];

    // Get recurrence pattern in weeks
    // @ts-expect-error FIXME: Property 'week' does not exist on type 'boolean | { week: number; }'.
    const eventRecurrenceWeeks: number = event.schedule?.every?.week || 1;

    // Iterate through all events in the array
    for (let i = 0; i < events?.length; i++) {
      const comparisonEvent = events[i];

      if (comparisonEvent?.data?.id === event?.data?.id) {
        continue;
      }

      const eventOrderTypes: CalendarEventIs = event.data?.is || {};
      const comparisonOrderTypes: CalendarEventIs = comparisonEvent.data?.is || {};

      if (!eventsOverlapByOrderType(eventOrderTypes, comparisonOrderTypes)) {
        continue;
      }

      // Get comparison event's overall date range
      const comparisonRangeStart: number = comparisonEvent.schedule?.start;
      const comparisonRangeEnd: number = comparisonEvent.schedule?.end;

      // Check if the date ranges overlap
      // Events don't overlap if one ends before the other starts
      if (eventRangeEnd && comparisonRangeStart && eventRangeEnd < comparisonRangeStart) {
        continue; // Event ends before comparison event starts
      }
      if (comparisonRangeEnd && eventRangeStart && comparisonRangeEnd < eventRangeStart) {
        continue; // Comparison event ends before event starts
      }

      // Get comparison event date/time information
      const comparisonStartDateTime = DateTime.fromISO(
        getEventDateStart(comparisonEvent.schedule, timezone),
      ).setZone('utc');
      const comparisonDuration: number = getEventDuration(comparisonEvent.schedule);
      const comparisonEndDateTime: DateTime = comparisonStartDateTime.plus({
        millisecond: comparisonDuration,
      });

      // Get weekdays (default to all days if not specified)
      const comparisonWeekdays: number[] = comparisonEvent.schedule?.dayOfWeek || [
        0, 1, 2, 3, 4, 5, 6,
      ];

      // Get recurrence pattern
      // @ts-expect-error FIXME: Property 'week' does not exist on type 'boolean | { week: number; }'.
      const comparisonRecurrenceWeeks: number = comparisonEvent.schedule?.every?.week || 1;

      // Check if events have different recurrence patterns (e.g., every 2 weeks)
      // If so, we need to verify the two calendar events occur in the same weeks
      const hasRecurrence: boolean = eventRecurrenceWeeks > 1 || comparisonRecurrenceWeeks > 1;
      let sameRecurrencePhase: boolean = true;

      if (hasRecurrence && eventRecurrenceWeeks === comparisonRecurrenceWeeks) {
        // Calculate which week each event starts in relative to a common reference point
        // Using Unix epoch (Jan 1, 1970) as the reference, which is a Thursday
        const epochStart: DateTime = DateTime.fromMillis(0, { zone: 'utc' });
        const eventWeeksSinceEpoch: number = Math.floor(
          eventStartDateTime.diff(epochStart, 'weeks').weeks,
        );
        const comparisonWeeksSinceEpoch: number = Math.floor(
          comparisonStartDateTime.diff(epochStart, 'weeks').weeks,
        );

        // Check if events are in phase (same week modulo recurrence period)
        sameRecurrencePhase =
          eventWeeksSinceEpoch % eventRecurrenceWeeks ===
          comparisonWeeksSinceEpoch % comparisonRecurrenceWeeks;
      }

      // Check for overlap only if events are in the same recurrence phase
      if (
        sameRecurrencePhase &&
        checkEventsOverlap(
          eventStartDateTime,
          eventEndDateTime,
          comparisonStartDateTime,
          comparisonEndDateTime,
          eventWeekdays,
          comparisonWeekdays,
        )
      ) {
        overlaps.push(comparisonEvent);
      }
    }

    return overlaps;
  };

  return {
    structureCalendarEvents,
    todaysEvents,
    isOneDayEvent,
    isOneDayEventScheduledForToday,
    isEventCurrent,
    getStartOfWeekTSFromMillis,
    isReoccuringEventScheduledForToday,
    getRepeatMessageFromEvent,
    getRepeatValueFromSchedule,
    prepMenuEventForSave,
    prepClosureEventForSave,
    everyWeekDaySchedule,
    formOverlapsWithMenuEvent,
    formOverlapsWithClosureEvent,
    detectEventOverlaps,
  };
};
